import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useCart } from '@/store/cart';
import { getInventory } from '@/lib/checkoutClient';
import { getSeatMap } from '@/lib/api/taquilla';
import { toast } from '@/components/ui';

/**
 * useCartImport — importa un carrito traído desde Web (OPERATOR #4, 2026-06-22).
 *
 * Web, al hacer "Ir a pagar", manda al usuario a:
 *
 *   {COMMERCE_URL}/cart?fc_cart=<base64url(json)>#fcsess=<base64url(json)>
 *
 * El fragmento `#fcsess` (sesión) lo procesa `sessionImport.ts` en el boot.
 * Acá procesamos el query param `fc_cart`, que trae las líneas seleccionadas
 * en Web:
 *
 *   { v: 1, lines: [{ eventId, localityId, seatIdentifier?, quantity }] }
 *
 * Web NO conoce el `stageId` (es interno de api-taquilla), así que lo
 * resolvemos acá contra el inventario/seat-map del evento (mismo mecanismo del
 * deep-link `intent=add-to-cart`). Una vez agregadas, limpiamos el param para
 * que un reload no re-importe.
 *
 * Diseño defensivo:
 *   - Idempotente vía `useRef` (no re-procesa en re-render / StrictMode doble
 *     mount: el flag + la limpieza del param lo cubren).
 *   - Si el evento / localidad / silla no resuelve, esa línea se saltea con un
 *     toast informativo; el resto del carrito se importa igual.
 *   - No pisa el carrito existente: hace `addLine` (que deduplica por
 *     evento+localidad+silla en el store).
 */

interface ImportLine {
  eventId: string;
  localityId: string;
  seatIdentifier?: string | null;
  quantity: number;
}

function b64urlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  return decodeURIComponent(escape(atob(b64 + pad)));
}

function parsePayload(raw: string | null): ImportLine[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(b64urlDecode(raw)) as { v?: number; lines?: unknown };
    if (!Array.isArray(parsed.lines)) return null;
    const lines: ImportLine[] = [];
    for (const l of parsed.lines as Record<string, unknown>[]) {
      if (
        l &&
        typeof l.eventId === 'string' &&
        typeof l.localityId === 'string' &&
        typeof l.quantity === 'number' &&
        l.quantity > 0
      ) {
        lines.push({
          eventId: l.eventId,
          localityId: l.localityId,
          seatIdentifier: typeof l.seatIdentifier === 'string' ? l.seatIdentifier : null,
          quantity: Math.min(Math.max(1, Math.floor(l.quantity)), 50),
        });
      }
    }
    return lines.length > 0 ? lines : null;
  } catch {
    return null;
  }
}

export function useCartImport(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCart();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    const raw = searchParams.get('fc_cart');
    if (!raw) return;
    processed.current = true;

    const lines = parsePayload(raw);

    // Limpiamos el param SIEMPRE (haya o no líneas válidas) para no re-importar.
    const next = new URLSearchParams(searchParams);
    next.delete('fc_cart');
    setSearchParams(next, { replace: true });

    if (!lines) {
      toast.error({
        title: 'No pudimos leer el carrito',
        description: 'Volvé a agregar tus boletas desde el evento.',
      });
      return;
    }

    void (async () => {
      // Resolvemos stageId por evento. Agrupamos por eventId para no pedir el
      // inventario/seat-map más de una vez por evento.
      const eventIds = [...new Set(lines.map((l) => l.eventId))];
      const inventoryByEvent = new Map<string, Awaited<ReturnType<typeof getInventory>> | null>();
      const seatStageByEvent = new Map<string, Map<string, string>>(); // eventId → (localityId → stageId)

      for (const eventId of eventIds) {
        try {
          inventoryByEvent.set(eventId, await getInventory(eventId));
        } catch {
          inventoryByEvent.set(eventId, null);
        }
        // Sólo cargamos seat-map si alguna línea de este evento es por-silla.
        if (lines.some((l) => l.eventId === eventId && l.seatIdentifier)) {
          try {
            const sm = await getSeatMap(eventId);
            const m = new Map<string, string>();
            for (const loc of sm.localities ?? []) {
              if (loc.stage_id) m.set(loc.id, loc.stage_id);
            }
            seatStageByEvent.set(eventId, m);
          } catch {
            /* sin seat-map → las líneas por-silla de este evento se saltean */
          }
        }
      }

      let added = 0;
      let skipped = 0;
      let lastEventId: string | null = null;

      for (const line of lines) {
        let stageId: string | null = null;

        if (line.seatIdentifier) {
          // Línea por-silla: el stageId sale del seat-map (localidad real → etapa).
          stageId = seatStageByEvent.get(line.eventId)?.get(line.localityId) ?? null;
        } else {
          // Línea por-localidad: el stageId sale del inventario.
          const inv = inventoryByEvent.get(line.eventId);
          if (inv) {
            for (const stage of inv.stages) {
              if (stage.localities.some((loc) => loc.localityId === line.localityId)) {
                stageId = stage.stageId;
                break;
              }
            }
          }
        }

        if (!stageId) {
          skipped += 1;
          continue;
        }

        cart.setEvent(line.eventId);
        cart.addLine({
          eventId: line.eventId,
          stageId,
          localityId: line.localityId,
          seatIdentifier: line.seatIdentifier ?? null,
          quantity: line.quantity,
        });
        added += 1;
        lastEventId = line.eventId;
      }

      if (lastEventId) cart.setEvent(lastEventId);

      if (added > 0) {
        toast.success({
          title: 'Carrito importado',
          description:
            skipped > 0
              ? `Agregamos ${added} ítem(s). ${skipped} no se pudieron resolver.`
              : `Agregamos ${added} ítem(s) a tu carrito.`,
        });
      } else {
        toast.error({
          title: 'No pudimos importar el carrito',
          description:
            'Las boletas seleccionadas ya no están disponibles. Probá de nuevo desde el evento.',
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
